import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Table, TableHead, TableBody, TableRow, TableCell, TablePagination, Stack, Box, Typography, Chip, Skeleton } from "@mui/material";
import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import { ReactComponent as BackIcon } from "../../../../../vendor/_new/icon/detections/back.svg";
import { getBotType } from "../../../application/analytics/common";

import { formatDate } from "../../../../../utils/format";
import colors from "../../../../../utils/colors";
import useEvent from "../../../../../hooks/user/useEvent";
import { WafAction } from "../../../../../utils/constants";

const tableHeadSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
const tableBodySx =  { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] };
const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

export const getCategoryTitle = (categoryId) => {
  switch (categoryId) {
    case "country":
      return "Top detections by country";
    case "ua":
      return "Top user agent";
    case "uri":
      return "Top paths";
    case "source_ip":
      return "Top events by source";
    case "method":
      return "Top methods";
    case "host":
      return "Top hosts";
    case "ja3_hash":
      return "Top JA3 fingerprints";
    default:
      return "Top detections by country";
  }
};

export const getEventTitle = (categoryId, value) => {
  if (categoryId === "country") {
    return regionNames.of(value || "US");
  } else if (categoryId === "ua" || categoryId === "uri") {
    return value?.length > 48 ? value?.substr(0, 48) + " ..." : value;
  } else {
    return value;
  }
};

export const SkeletonContent = (props) => {
  const { rowsPerPage } = props;

  var rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }

  var cellArray = [0, 1, 2, 3, 4, 5, 6, 7];

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell
              sx={{
                padding: "16px 8px",
              }}
              key={"s_" + r + "_" + c}
            >
              <Skeleton
                height="22px"
                width="100%"
                py="5px"
                variant="rectangular"
                sx={{
                  borderRadius: "11px",
                }}
              />
            </TableCell>
          ))}
          <TableCell />
        </TableRow>
      ))}
    </TableBody>
  );
};

const BotDetectionDetail = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();

  const { botEvents, total_bot_events_count, from, rows_per_page, getOnlyBotEvents } = useEvent();

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const parseResult = JSON.parse(decodeURIComponent(searchParams.get("data")));
  const siteID = parseResult?.siteID || "all";
  const timeRange = parseResult?.timeRange || "7d";
  const data = parseResult?.data;

  useEffect(() => {
    setPage(from / rows_per_page);
  }, [from]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setRowsPerPage(rows_per_page);
  }, [rows_per_page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getOnlyBotEvents(siteID, timeRange, 5, 0, WafAction.ALL, [{ key: categoryId, values: [data], condition: "eq" }]);
    // eslint-disable-next-line
  }, [categoryId, getOnlyBotEvents]);

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    getOnlyBotEvents(siteID, timeRange, rowsPerPage, rowsPerPage * newPage, WafAction.ALL, [
      { key: categoryId, values: [data], condition: "eq" },
    ]);
  };

  const handleChangeRowsPerPage = async (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
    getOnlyBotEvents(siteID, timeRange, parseInt(event.target.value, 10), 0, WafAction.ALL, [
      { key: categoryId, values: [data], condition: "eq" },
    ]);
  };

  return (
    <>
      <Helmet title="Bot Management" />
      <Stack spacing={6} sx={{ p: 6 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={() =>
            navigate(`/v2/detection/bot/dashboard/${categoryId}?data=${encodeURIComponent(JSON.stringify({ siteID, timeRange }))}`)
          }
          sx={{ cursor: "pointer" }}
        >
          <BackIcon style={{ color: colors.primary[300] }} />
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
            Bot Management Dashboard
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
            /
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
            {getCategoryTitle(categoryId)}
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
            /
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
            {getEventTitle(categoryId, data)}
          </Typography>
        </Stack>
        <Stack>
          <Box spacing={6} sx={{ border: `solid 1px ${colors.neutral[200]}`, borderRadius: "8px", p: 2 }}>
            <Table
              sx={{
                "& .MuiTableCell-head": { py: 3, px: 3 },
                "& .MuiTableCell-body": { py: 2, px: 3 },
                "& .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Timestamp</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Source IP</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Hostname</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>URI</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>User agent</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Bot type</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Response status</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>HTTP method</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}></Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              {botEvents === null && <SkeletonContent rowsPerPage={rowsPerPage} />}
              <TableBody>
                {botEvents?.map((item, index) => (
                  <TableRow key={`detail_data_${index}`}>
                    <TableCell>
                      <Typography sx={tableBodySx}>{formatDate(item?.timestamp) || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{item?.src_ip}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{item?.host_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>
                        {item?.uri && item?.uri.length > 24 ? item?.uri.substr(0, 24) + " ..." : item?.uri}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>
                        {item?.ua && item?.ua.length > 24 ? item?.ua.substr(0, 24) + " ..." : item?.ua}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getBotType(item?.bot_score)}
                        sx={{ px: 1, background: colors.yellow[100], borderRadius: "30px", ...tableBodySx }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{item?.resStatus}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{item?.method}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack
                        alignItems={"flex-end"}
                        onClick={() => navigate(`/v2/detection/bot_event/view/${categoryId}/${item?.id}?data=${encodeURIComponent(data)}`)}
                        sx={{ cursor: "pointer" }}
                      >
                        <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {botEvents?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography sx={tableBodySx} textAlign="center">
                        There are no events
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component="div"
            count={total_bot_events_count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-selectLabel": { },
              "& .MuiTablePagination-displayedRows": { },
              "& .MuiInputBase-root-MuiTablePagination-select": { },
              "& .MuiTablePagination-select":  { fontSize: "0.875rem" },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiList-root": {
                      py: 0,
                    },
                    "& .MuiMenuItem-root": {
                      fontSize: "0.875rem",
                      height: 32,
                    },
                  },
                },
              },
            }}
          />
        </Stack>
      </Stack>
    </>
  );
};
export default BotDetectionDetail;
